import { encodeAllURLParameters } from "@/common/api.utilities";
import { httpGet } from "@/services/api.service";
import { ENDPOINT_DISCOVERAI } from "@/common/constants/api";
import { getCountryLookup, getEventCountries } from "@/common/maps/utilities";

export { _getSuperClusterHeadlines as getSuperClusterHeadlines };

/**
 * Gets Super Clusters Headlines
 * Expected params:{ dateRange, maxAge, isOngoing, isAccelerating, isNew, useSecondaryCountries, similarity, aoiIds, topic, keywordSearch, pageOffset, pageSize, sortBy }
 * @param params
 * @param requestTimestamp
 * @returns {Promise<any|null>}
 */
const _getSuperClusterHeadlines = async (params, requestTimestamp) => {
  try {
    const url = encodeAllURLParameters({
      baseEndpoint: ENDPOINT_DISCOVERAI,
      params: _formatParams(params),
    });
    const result = await httpGet({ url });
    const { data } = JSON.parse(result);

    await _formatFeatures(data.features, requestTimestamp);

    return data;
  } catch (error) {
    console.error(
      "Issue getting super clusters headlines in superCluster.service.js:",
      error
    );
    return null;
  }
};

/**
 * Format/Validate/Cleanup params
 * Expected params:{ dateRange, maxAge, isOngoing, isAccelerating, isNew, useSecondaryCountries, similarity, aoiIds, topic, keywordSearch, pageOffset, pageSize, sortBy }
 * @param params
 * @returns {Object}
 */
const _formatParams = (params) => {
  return {
    dateRange: params.dateRange ?? "24h",
    maxAge: params.maxAge ?? null,
    isOngoing: params.isOngoing ?? false,
    isAccelerating: params.isAccelerating ?? false,
    isNew: params.isNew ?? false,
    similarity: params.similarity ?? null,
    useSecondaryCountries: params.useSecondaryCountries ?? false,
    aoiIds: params.aoiId ?? null,
    topic: params.topic ?? null,
    keywordSearch: params.keywordSearch ?? null,
    pageSize: params.pageSize ?? 100,
    pageOffset: params.pageOffset ?? 0,
    sortBy: params.sortBy ?? "size",
  };
};

const _formatFeatures = async (features, requestTimestamp) => {
  const countryLookup = await getCountryLookup();
  for (const feature of features) {
    await _formatFeature(feature, requestTimestamp, countryLookup);
  }
};

/**
 *
 * @param feature
 * @param requestTimestamp
 * @param countryLookup
 * @returns {Promise<void>}
 */
const _formatFeature = async (feature, requestTimestamp, countryLookup) => {
  if (feature.properties) {
    feature.properties.formatted = {};
    const countriesUpperCase = feature.properties._source[
      "24h"
    ].secondary_countries_list
      .filter((country) => country !== feature.properties._source.headline_cc)
      .map((country) => country?.toUpperCase());
    feature.properties.formatted.language =
      feature.properties._source.language ?? "en";
    feature.properties.formatted.requestTimestamp = requestTimestamp
      ? requestTimestamp
      : new Date().toISOString();
    feature.properties.formatted.esId = feature.properties._source.id ?? null;
    feature.properties.formatted.title =
      feature.properties._source.headline_title;

    feature.properties.formatted.occurred =
      feature.properties["@timestamp"] ?? null;

    feature.properties.formatted.newestDate =
      feature.properties._source.newest_date ?? null;

    feature.properties.formatted.type = "headlines";

    feature.properties.formatted.tagName = feature.properties._source.tag_name;
    feature.properties.formatted.is_new = feature.properties._source.is_new;
    feature.properties.formatted.is_accelerating =
      feature.properties._source.is_accelerating;

    feature.properties.formatted.eventsAI =
      feature.properties._source["24h"].deduplicate_counts + " Events AI";

    feature.properties.formatted.country = Object.keys(countryLookup)
      .filter(
        (key) =>
          key.toUpperCase() ===
          feature.properties._source.headline_cc?.toUpperCase()
      )
      .map((key) => countryLookup[key]);

    feature.properties.formatted.secondaryCountries = getEventCountries(
      countriesUpperCase,
      countryLookup
    );
  }
};
